import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/** Settings → Markets. One market = one region: countries, currency (USD for now), tax display. */
export type Market = {
  id: string
  name: string
  status: "active" | "draft"
  primary: boolean
  countries: string[]
  currency_code: string
  tax_inclusive: boolean
  duties_notice: boolean
  created_at: string | null
}
export type MarketInput = Pick<Market, "name" | "status" | "countries" | "tax_inclusive" | "duties_notice">

export function useMarkets() {
  return useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await sdk.client.fetch<{ markets: Market[] }>("/admin/terify/markets")).markets,
    staleTime: 60_000,
  })
}

/** Countries the store sells to (active markets), lowercase. Undefined while loading. */
export function useSellingCountries(): string[] | undefined {
  const { data } = useMarkets()
  return data ? data.filter((m) => m.status === "active").flatMap((m) => m.countries) : undefined
}

export function useSaveMarket(id?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: MarketInput) =>
      (await sdk.client.fetch<{ market: Market }>(id ? `/admin/terify/markets/${id}` : "/admin/terify/markets", { method: "POST", body })).market,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["markets"] })
      qc.invalidateQueries({ queryKey: ["settings", "taxes"] })
    },
  })
}

export function useDeleteMarket(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/markets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["markets"] })
      qc.invalidateQueries({ queryKey: ["settings", "taxes"] })
    },
  })
}

/** "Germany, France and 3 more" */
export function summarizeCountries(names: string[], max = 3) {
  if (!names.length) return "No countries/regions"
  if (names.length <= max) return names.join(", ")
  return `${names.slice(0, max).join(", ")} and ${names.length - max} more`
}
